/**
 * Client-side tracking/functionality cookie + script gate.
 *
 * Analytics (denied until opt-in):
 *   cookies: sbjs_*, _ga / _ga_*, Hotjar _hjSession*, Yandex _ym_*, mdd
 *   scripts: Order Attribution, gtag G-, GTM, GA collect, Hotjar, Yandex Metrika
 *
 * Advertisement (denied until opt-in):
 *   cookies: _gcl_au, _fbp, _ttp, ttcsid*, _tt_enable_cookie
 *   scripts: gtag AW-, doubleclick conversion, Meta fbevents, TikTok pixel/events
 *
 * Functionality (denied until opt-in):
 *   cookies: woocommerce_recently_viewed, g_state (Google One Tap / Sign-In)
 *   scripts: accounts.google.com/gsi/client, google-site-kit sign-in-with-google
 *
 * Starts strict opt-in. Stashes matching scripts until category unlock, then category-scoped
 * replay via flush(). Does NOT touch commercekit-nonce* or other woocommerce_* cart cookies.
 *
 * Filename kept as sbjs-blocker.ts for import stability.
 */

export type ConsentCategoryState = {
  analytics: boolean;
  advertisement: boolean;
  functionality: boolean;
};

/**
 * A site-specific script source to gate, matched as a case-insensitive
 * substring of the script's src.
 *
 * Needed because Google's first-party mode (Google tag gateway) serves the
 * GTM/GA4/Ads loaders from the site's own domain under a per-site random path:
 *
 *   https://www.example.com/mbw3/mh1Ekndv0MkcTQsXOGTtRGdwA5V0UlXn_v6ajwGELEm-WEkULQ
 *
 * There is no recognisable host or filename left to match on, so the built-in
 * host rules cannot see it. The path prefix differs per site, so it has to come
 * from configuration rather than from this file.
 */
export type ScriptSourcePattern = {
  /** Substring of the script src, e.g. "/mbw3/". */
  pattern: string;
  category: GateCategory;
};

export type TrackingBlockerApi = {
  /** Update category gates; cleans denied cookies; flushes allowed scripts. */
  syncConsent(state: ConsentCategoryState): void;
  /**
   * Register site-specific script sources. Safe to call more than once; the
   * synchronous sources (embed attribute, cached config) run first and the
   * fetched configuration tops them up.
   */
  setScriptPatterns(patterns: ScriptSourcePattern[]): void;
  flush(): void;
  isBlocked(cookieName: string): boolean;
};

type BlockedScriptSnapshot = {
  src: string | null;
  type: string | null;
  async: boolean;
  defer: boolean;
  text: string;
  attrs: Array<[string, string]>;
  /** Category that must be allowed before this script may run. */
  category: GateCategory;
  /** Monotonic stash order — flush replays allowed scripts in this order. */
  originalIndex: number;
};

type CookieSetter = (this: Document, value: string) => void;
type CookieGetter = (this: Document) => string;
type GateCategory = 'analytics' | 'advertisement' | 'functionality';

/** Exact names always cleaned when analytics is denied. */
const ANALYTICS_COOKIE_NAMES = [
  'sbjs_session',
  'sbjs_current_add',
  'sbjs_first_add',
  'sbjs_current',
  'sbjs_first',
  'sbjs_udata',
  'sbjs_migrations',
  '_ga',
  'mdd',
] as const;

/** Exact names always cleaned when advertisement is denied. */
const ADVERTISEMENT_COOKIE_NAMES = [
  '_gcl_au',
  '_fbp',
  '_ttp',
  '_tt_enable_cookie',
] as const;

/**
 * Exact names always cleaned when functionality is denied.
 * g_state = Google One Tap / Sign-In state (not ad/analytics tracking).
 */
const FUNCTIONALITY_COOKIE_NAMES = ['woocommerce_recently_viewed', 'g_state'] as const;

const CLEANUP_SWEEP_MS = [0, 50, 250, 1000, 3000] as const;

function cookieNameFromSetString(value: string): string {
  const m = String(value).match(/^\s*([^=;\s]+)/);
  return m?.[1] ?? '';
}

/** Normalize to bare cookie name (handles "name=value; path=/" set-strings). */
function bareCookieName(nameOrSetString: string): string {
  const raw = String(nameOrSetString).trim();
  if (!raw.includes('=') && !raw.includes(';')) return raw;
  return cookieNameFromSetString(raw);
}

/**
 * True when name is analytics-gated tracking.
 * _ga exact + _ga_* measurement IDs; Hotjar session; Yandex Metrika (_ym_*, mdd); sbjs.
 * Note: g_state is functionality (Google Sign-In), not analytics.
 */
function isAnalyticsCookieName(name: string): boolean {
  if (!name) return false;
  if (/^sbjs_/i.test(name)) return true;
  if (/^_ga$/i.test(name) || /^_ga_/i.test(name)) return true;
  if (/^_hjSessionUser_/i.test(name) || /^_hjSession_/i.test(name)) return true;
  if (/^_ym_/i.test(name)) return true;
  if (/^mdd$/i.test(name)) return true;
  return false;
}

/** True when name is advertisement-gated tracking. */
function isAdvertisementCookieName(name: string): boolean {
  if (!name) return false;
  if (/^_gcl_au$/i.test(name)) return true;
  if (/^_fbp$/i.test(name)) return true;
  if (/^_ttp$/i.test(name)) return true;
  if (/^_tt_enable_cookie$/i.test(name)) return true;
  if (/^ttcsid/i.test(name)) return true; // ttcsid + ttcsid_*
  return false;
}

/**
 * Exact functionality cookies only — never a broad woocommerce_ prefix.
 * g_state: Google One Tap / GSI client state.
 */
function isFunctionalityCookieName(name: string): boolean {
  if (!name) return false;
  if (/^woocommerce_recently_viewed$/i.test(name)) return true;
  // The Product Recommendations plugin keeps its own recently-viewed list
  // under a different name. Same job, different plugin: uygunkitapal.com sets
  // this one and not the core cookie, so listing only the core name left it
  // ungated. Written from a static plugin script the blocker cannot stop from
  // loading, which is exactly why the cookie gate exists alongside it.
  if (/^wc_prl_recently_viewed$/i.test(name)) return true;
  if (/^g_state$/i.test(name)) return true;
  return false;
}

/**
 * Map a cookie name (or Set-Cookie name=value prefix) to the CMP category gate.
 * Unknown cookies → null (never blocked — e.g. commercekit-nonce*, cart cookies).
 */
export function categoryForCookie(nameOrSetString: string): GateCategory | null {
  const name = bareCookieName(nameOrSetString);
  if (isAnalyticsCookieName(name)) return 'analytics';
  if (isAdvertisementCookieName(name)) return 'advertisement';
  if (isFunctionalityCookieName(name)) return 'functionality';
  return null;
}

/** Site-specific sources, registered from configuration at runtime. */
let extraScriptPatterns: ScriptSourcePattern[] = [];

/**
 * Shortest accepted pattern.
 *
 * A one or two character substring ("/", ".js") would match nearly every
 * script on the page, including the CMP's own bundle, and take the site down
 * instead of gating a tag. Configuration is trusted but not infallible.
 */
const MIN_PATTERN_LENGTH = 3;

export function setExtraScriptPatterns(patterns: ScriptSourcePattern[]): void {
  const valid: ScriptSourcePattern[] = [];

  for (const entry of patterns) {
    const pattern = String(entry?.pattern ?? '').trim().toLowerCase();

    if (pattern.length < MIN_PATTERN_LENGTH) {
      try {
        console.warn('[Yargı CMP] Script deseni çok kısa, yok sayıldı:', entry?.pattern);
      } catch {
        // ignore
      }
      continue;
    }

    const category = entry.category;
    if (category !== 'analytics' && category !== 'advertisement' && category !== 'functionality') {
      continue;
    }

    valid.push({ pattern, category });
  }

  extraScriptPatterns = valid;
}

export function getExtraScriptPatterns(): ScriptSourcePattern[] {
  return extraScriptPatterns;
}

/**
 * Classify a script element for stash/replay.
 * Returns the gate category, or null if the script should pass through.
 *
 * Matching is conservative (URL path / known hosts) to avoid false positives
 * on first-party app scripts.
 */
export function categoryForScript(el: HTMLScriptElement): GateCategory | null {
  const srcAttr = `${el.getAttribute('src') || ''} ${el.src || ''}`.trim();
  const src = srcAttr.toLowerCase();
  const id = `${el.id || ''} ${el.getAttribute('id') || ''}`.toLowerCase();
  const text = (el.textContent || '').toLowerCase();
  const hay = `${src} ${id} ${text}`;

  // Site-specific sources first: a first-party proxied tag has no other
  // distinguishing mark, so nothing below would ever match it.
  if (src) {
    for (const entry of extraScriptPatterns) {
      if (src.includes(entry.pattern)) {
        return entry.category;
      }
    }
  }

  // --- Functionality: Google Sign-In / One Tap / Site Kit SIWG ---
  if (
    /accounts\.google\.com\/gsi\/client/.test(src) ||
    /google-site-kit[^"'\\\s]*sign-in-with-google/.test(hay) ||
    /sign-in-with-google/.test(src) ||
    /sign-in-with-google/.test(id)
  ) {
    return 'functionality';
  }

  // --- Advertisement: Ads / Meta / TikTok ---
  // AW- is Google Ads, DC- is Floodlight; both are advertising rather than
  // measurement, so they answer to that category before the analytics rules.
  if (/googletagmanager\.com\/gtag\/js\?[^"'\\\s]*id=(aw|dc)-/.test(src)) {
    return 'advertisement';
  }
  if (/googleads\.g\.doubleclick\.net\/pagead\/viewthroughconversion/.test(src)) {
    return 'advertisement';
  }
  if (/connect\.facebook\.net\/[^"'\\\s]*\/fbevents\.js/.test(src) || /fbevents\.js/.test(src)) {
    return 'advertisement';
  }
  if (
    /analytics\.tiktok\.com\/[^"'\\\s]*\/(pixel|events\.js)/.test(src) ||
    /analytics\.tiktok\.com/.test(src)
  ) {
    return 'advertisement';
  }
  if (/fbq\s*\(|facebook\.net\/en_us\/fbevents|ttq\.load|tiktok.*pixel/.test(text)) {
    if (/fbq\s*\(|fbevents|ttq\.|tiktok/.test(text)) {
      return 'advertisement';
    }
  }

  // --- Analytics: GA4, GTM, Hotjar, Yandex, Order Attribution, Site Kit events ---
  //
  // Both the GA4 measurement ID (G-) and the unified Google tag ID (GT-). The
  // GT- form was missed for a while: "id=g-" does not match "id=gt-" because
  // the hyphen has to follow the g. That single gap let the whole tag chain
  // load on ucuzkitap.com after a rejection — gtag.js came in, GA4 started, and
  // kept sending cookieless /g/collect pings while every other Google source
  // was correctly gated.
  //
  // A GT- tag can drive both GA4 and Ads. Gating it under analytics is the
  // strict-opt-in reading: nothing loads until analytics is accepted.
  //
  // "ua-" is the legacy Universal Analytics id; it does not match "g-"/"gt-"
  // for the same reason GT- did not match G-, so it is named explicitly.
  if (/googletagmanager\.com\/gtag\/js\?[^"'\\\s]*id=(g|gt|ua)-/.test(src)) {
    return 'analytics';
  }
  if (/googletagmanager\.com\/gtm\.js/.test(src)) {
    return 'analytics';
  }
  if (
    /google-analytics\.com\/g\/collect/.test(src) ||
    /google-analytics\.com\/analytics\.js/.test(src)
  ) {
    return 'analytics';
  }
  if (/static\.hotjar\.com\/c\/hotjar-/.test(src) || /static\.hotjar\.com/.test(src)) {
    return 'analytics';
  }
  if (/mc\.yandex\.(ru|com)\/metrika\/tag\.js/.test(src) || /mc\.yandex\.(ru|com)/.test(src)) {
    return 'analytics';
  }
  // Google Site Kit event providers / modules (not SIWG — handled above).
  // Includes inline `*-js-before` companions that touch window._googlesitekit.
  if (
    /google-site-kit|googlesitekit/.test(src) ||
    /googlesitekit/.test(id) ||
    /_googlesitekit/.test(text)
  ) {
    return 'analytics';
  }
  if (
    /(?:^|\/)(?:sourcebuster|wc-order-attribution|order-attribution)(?:\.|\/|$|\?)/.test(src) ||
    /sourcebuster|wc-order-attribution|order-attribution/.test(src) ||
    /sbjs_|sourcebuster|order\s*attribution|wc_order_attribution/.test(text)
  ) {
    return 'analytics';
  }
  if (/gtag\s*\(\s*['"]config['"]\s*,\s*['"]g-/.test(text)) {
    return 'analytics';
  }
  if (/gtag\s*\(\s*['"]config['"]\s*,\s*['"]aw-/.test(text)) {
    return 'advertisement';
  }

  return null;
}

function snapshotScript(
  el: HTMLScriptElement,
  category: GateCategory,
  originalIndex: number,
): BlockedScriptSnapshot {
  const attrs: Array<[string, string]> = [];
  for (const attr of Array.from(el.attributes)) {
    attrs.push([attr.name, attr.value]);
  }
  return {
    src: el.getAttribute('src'),
    type: el.getAttribute('type'),
    async: el.async,
    defer: el.defer,
    text: el.textContent || '',
    attrs,
    category,
    originalIndex,
  };
}

function recreateScript(snap: BlockedScriptSnapshot): HTMLScriptElement {
  const el = document.createElement('script');
  for (const [name, value] of snap.attrs) {
    if (name === 'src') continue;
    try {
      el.setAttribute(name, value);
    } catch {
      // ignore
    }
  }
  if (snap.text) el.textContent = snap.text;
  if (snap.src) el.src = snap.src;
  el.async = snap.async;
  el.defer = snap.defer;
  return el;
}

function cookieDomainCandidates(hostname: string): Array<string | null> {
  const out: Array<string | null> = [null];
  if (!hostname || hostname === 'localhost' || /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) {
    return out;
  }

  const variants = new Set<string>();
  const add = (h: string) => {
    if (!h) return;
    const bare = h.replace(/^\./, '');
    variants.add(bare);
    variants.add(`.${bare}`);
  };

  add(hostname);
  if (hostname.startsWith('www.')) add(hostname.slice(4));
  else add(`www.${hostname}`);

  const parts = hostname.replace(/^\./, '').split('.');
  for (let i = 1; i < parts.length - 1; i++) {
    add(parts.slice(i).join('.'));
  }

  for (const v of variants) out.push(v);
  return out;
}

function discoverNamesFromDocument(predicate: (name: string) => boolean): string[] {
  if (typeof document === 'undefined') return [];
  const names: string[] = [];
  const raw = document.cookie || '';
  if (!raw) return names;
  for (const part of raw.split(';')) {
    const name = part.split('=')[0]?.trim() ?? '';
    if (name && predicate(name)) names.push(name);
  }
  return names;
}

function expireCookieNames(names: Iterable<string>, nativeSet?: (value: string) => void): void {
  if (typeof document === 'undefined') return;

  const setCookie =
    nativeSet ??
    ((value: string) => {
      document.cookie = value;
    });

  const expire = 'Thu, 01 Jan 1970 00:00:00 GMT';
  const hostname = typeof location !== 'undefined' ? location.hostname : '';
  const domains = cookieDomainCandidates(hostname);
  const paths = ['/', ''];
  if (typeof location !== 'undefined' && location.pathname && location.pathname !== '/') {
    paths.push(location.pathname);
    const parentPath = location.pathname.replace(/\/[^/]*$/, '') || '/';
    if (parentPath && !paths.includes(parentPath)) paths.push(parentPath);
  }

  for (const name of names) {
    for (const path of paths) {
      const pathPart = path === '' ? '' : `; path=${path}`;
      for (const domain of domains) {
        const domainPart = domain ? `; domain=${domain}` : '';
        setCookie(`${name}=; expires=${expire}${pathPart}${domainPart}`);
        setCookie(`${name}=; Max-Age=0${pathPart}${domainPart}`);
        setCookie(`${name}=; expires=${expire}${pathPart}${domainPart}; SameSite=Lax`);
        setCookie(`${name}=; expires=${expire}${pathPart}${domainPart}; SameSite=None; Secure`);
      }
    }
  }
}

export function deleteSbjsCookies(nativeSet?: (value: string) => void): void {
  const names = new Set<string>([
    ...ANALYTICS_COOKIE_NAMES,
    ...discoverNamesFromDocument((n) => isAnalyticsCookieName(n)),
  ]);
  expireCookieNames(names, nativeSet);
}

function readCookieDescriptor(target: object): PropertyDescriptor | undefined {
  return Object.getOwnPropertyDescriptor(target, 'cookie');
}

function patchCookieAccessor(
  target: object,
  shouldBlockWrite: (value: string) => boolean,
  nativeSetRef: { current: CookieSetter | null },
): boolean {
  const desc = readCookieDescriptor(target);
  if (!desc) return false;
  if ((target as { __yargiSbjsCookiePatched?: boolean }).__yargiSbjsCookiePatched) {
    return true;
  }

  let getter: CookieGetter | undefined = typeof desc.get === 'function' ? desc.get : undefined;
  let setter: CookieSetter | undefined = typeof desc.set === 'function' ? desc.set : undefined;

  if (!getter || !setter) {
    let proto = Object.getPrototypeOf(target) as object | null;
    while (proto && (!getter || !setter)) {
      const pDesc = readCookieDescriptor(proto);
      if (pDesc) {
        if (!getter && typeof pDesc.get === 'function') getter = pDesc.get;
        if (!setter && typeof pDesc.set === 'function') setter = pDesc.set;
      }
      proto = Object.getPrototypeOf(proto);
    }
  }

  if (!getter || !setter) return false;
  if (!nativeSetRef.current) nativeSetRef.current = setter;

  if (desc.configurable === false && Object.prototype.hasOwnProperty.call(target, 'cookie')) {
    return false;
  }

  try {
    Object.defineProperty(target, 'cookie', {
      configurable: true,
      enumerable: desc.enumerable ?? true,
      get() {
        return getter!.call(this);
      },
      set(value: string) {
        if (shouldBlockWrite(String(value))) return;
        setter!.call(this, value);
      },
    });
    try {
      Object.defineProperty(target, '__yargiSbjsCookiePatched', {
        value: true,
        configurable: true,
      });
    } catch {
      // ignore
    }
    return true;
  } catch {
    return false;
  }
}

export function createSbjsBlocker(): TrackingBlockerApi {
  /** Our own bundle's URL, captured while document.currentScript still points at it. */
  const ownScriptSrc =
    typeof document !== 'undefined' && document.currentScript instanceof HTMLScriptElement
      ? document.currentScript.src
      : '';

  let installed = false;
  let analyticsAllowed = false;
  let advertisementAllowed = false;
  let functionalityAllowed = false;
  /** Monotonic counter for stash order across categories. */
  let stashSeq = 0;

  const blockedScripts: BlockedScriptSnapshot[] = [];
  const seen = new WeakSet<HTMLScriptElement>();
  const sweepTimers: number[] = [];
  const nativeSetRef: { current: CookieSetter | null } = { current: null };

  let originalAppendChild: typeof Node.prototype.appendChild | null = null;
  let originalInsertBefore: typeof Node.prototype.insertBefore | null = null;
  let originalReplaceChild: typeof Node.prototype.replaceChild | null = null;
  let originalElementAppend: typeof Element.prototype.append | null = null;
  let originalElementPrepend: typeof Element.prototype.prepend | null = null;
  let observer: MutationObserver | null = null;

  function isCategoryAllowed(cat: GateCategory): boolean {
    if (cat === 'analytics') return analyticsAllowed;
    if (cat === 'advertisement') return advertisementAllowed;
    return functionalityAllowed;
  }

  function shouldBlockCookieWrite(value: string): boolean {
    const cat = categoryForCookie(value);
    if (!cat) return false;
    return !isCategoryAllowed(cat);
  }

  function isBlockedName(cookieName: string): boolean {
    const cat = categoryForCookie(cookieName);
    if (!cat) return false;
    return !isCategoryAllowed(cat);
  }

  function stash(el: HTMLScriptElement, category: GateCategory): void {
    if (seen.has(el)) return;
    seen.add(el);
    blockedScripts.push(snapshotScript(el, category, stashSeq++));
    el.remove();
  }

  function maybeBlockScript(node: Node): boolean {
    if (!(node instanceof HTMLScriptElement)) return false;
    // Never gate our own bundle: a misconfigured site pattern would otherwise
    // stash the CMP itself and leave the page with no consent UI at all.
    if (ownScriptSrc && node.src && node.src === ownScriptSrc) return false;
    const category = categoryForScript(node);
    if (!category) return false;
    if (isCategoryAllowed(category)) return false;
    stash(node, category);
    return true;
  }

  function maybeBlockInTree(node: Node): void {
    if (node instanceof HTMLScriptElement) {
      maybeBlockScript(node);
    } else if (node instanceof Element || node instanceof DocumentFragment) {
      for (const script of Array.from(node.querySelectorAll('script'))) {
        maybeBlockScript(script);
      }
    }
  }

  function nativeDelete(value: string): void {
    if (nativeSetRef.current) {
      nativeSetRef.current.call(document, value);
    } else {
      document.cookie = value;
    }
  }

  function cleanupDeniedCookies(): void {
    const names = new Set<string>();

    if (!analyticsAllowed) {
      for (const n of ANALYTICS_COOKIE_NAMES) names.add(n);
      for (const n of discoverNamesFromDocument(isAnalyticsCookieName)) {
        names.add(n);
      }
    }

    if (!advertisementAllowed) {
      for (const n of ADVERTISEMENT_COOKIE_NAMES) names.add(n);
      for (const n of discoverNamesFromDocument(isAdvertisementCookieName)) {
        names.add(n);
      }
    }

    if (!functionalityAllowed) {
      for (const n of FUNCTIONALITY_COOKIE_NAMES) names.add(n);
      for (const n of discoverNamesFromDocument(isFunctionalityCookieName)) {
        names.add(n);
      }
    }

    if (names.size === 0) return;
    expireCookieNames(names, (value) => nativeDelete(value));
  }

  function clearSweepTimers(): void {
    for (const id of sweepTimers) clearTimeout(id);
    sweepTimers.length = 0;
  }

  function allGatesOpen(): boolean {
    return analyticsAllowed && advertisementAllowed && functionalityAllowed;
  }

  function scheduleCleanupSweeps(): void {
    clearSweepTimers();
    if (typeof window === 'undefined') {
      cleanupDeniedCookies();
      return;
    }
    if (allGatesOpen()) return;

    for (const ms of CLEANUP_SWEEP_MS) {
      const id = window.setTimeout(() => {
        if (allGatesOpen()) return;
        cleanupDeniedCookies();
      }, ms);
      sweepTimers.push(id);
    }
  }

  /**
   * Ensure globals that Site Kit / companion before-scripts expect exist
   * so a single TypeError does not abort the rest of the replay chain.
   */
  function bootstrapReplayGlobals(): void {
    if (typeof window === 'undefined') return;
    try {
      const w = window as Window & { _googlesitekit?: Record<string, unknown> };
      if (!w._googlesitekit || typeof w._googlesitekit !== 'object') {
        w._googlesitekit = {};
      }
    } catch {
      // ignore
    }
  }

  /**
   * Replay only scripts whose category gate is now open, in original stash order.
   * Denied-category snapshots stay queued for a later unlock.
   * Each script is isolated so one throw does not skip the rest.
   */
  function flushAllowed(): void {
    if (typeof document === 'undefined') return;
    bootstrapReplayGlobals();

    const parent = document.head || document.documentElement;
    const keep: BlockedScriptSnapshot[] = [];
    const toReplay: BlockedScriptSnapshot[] = [];

    for (const snap of blockedScripts) {
      if (isCategoryAllowed(snap.category)) {
        toReplay.push(snap);
      } else {
        keep.push(snap);
      }
    }

    // Preserve discovery order (inline before + companion external stay adjacent).
    toReplay.sort((a, b) => a.originalIndex - b.originalIndex);

    for (const snap of toReplay) {
      try {
        parent.appendChild(recreateScript(snap));
      } catch (err) {
        try {
          console.error('[Yargı CMP Replay Error]', snap.src || snap.attrs.find((a) => a[0] === 'id')?.[1] || '(inline)', err);
        } catch {
          // ignore logging failures
        }
      }
    }

    blockedScripts.length = 0;
    // Keep remaining denied scripts in original order.
    keep.sort((a, b) => a.originalIndex - b.originalIndex);
    blockedScripts.push(...keep);
  }

  function installCookiePatches(): void {
    const targets: object[] = [];
    if (typeof Document !== 'undefined') targets.push(Document.prototype);
    if (typeof HTMLDocument !== 'undefined') targets.push(HTMLDocument.prototype);
    if (typeof document !== 'undefined') targets.push(document);

    const seenTargets = new Set<object>();
    for (const t of targets) {
      if (!t || seenTargets.has(t)) continue;
      seenTargets.add(t);
      patchCookieAccessor(t, shouldBlockCookieWrite, nativeSetRef);
    }
  }

  function install(): void {
    if (installed || typeof document === 'undefined') return;
    installed = true;

    installCookiePatches();

    originalAppendChild = Node.prototype.appendChild;
    originalInsertBefore = Node.prototype.insertBefore;
    originalReplaceChild = Node.prototype.replaceChild;

    Node.prototype.appendChild = function <T extends Node>(child: T): T {
      if (maybeBlockScript(child)) return child;
      maybeBlockInTree(child);
      return originalAppendChild!.call(this, child) as T;
    };

    Node.prototype.insertBefore = function <T extends Node>(newNode: T, ref: Node | null): T {
      if (maybeBlockScript(newNode)) return newNode;
      maybeBlockInTree(newNode);
      return originalInsertBefore!.call(this, newNode, ref) as T;
    };

    Node.prototype.replaceChild = function <T extends Node>(newChild: Node, oldChild: T): T {
      if (maybeBlockScript(newChild)) return oldChild;
      maybeBlockInTree(newChild);
      return originalReplaceChild!.call(this, newChild, oldChild) as T;
    };

    if (typeof Element !== 'undefined' && Element.prototype.append) {
      originalElementAppend = Element.prototype.append;
      originalElementPrepend = Element.prototype.prepend;

      Element.prototype.append = function (...nodes: Array<Node | string>): void {
        const allowed: Array<Node | string> = [];
        for (const n of nodes) {
          if (typeof n !== 'string' && maybeBlockScript(n)) continue;
          if (typeof n !== 'string') maybeBlockInTree(n);
          allowed.push(n);
        }
        return originalElementAppend!.apply(this, allowed as [Node | string]);
      };

      Element.prototype.prepend = function (...nodes: Array<Node | string>): void {
        const allowed: Array<Node | string> = [];
        for (const n of nodes) {
          if (typeof n !== 'string' && maybeBlockScript(n)) continue;
          if (typeof n !== 'string') maybeBlockInTree(n);
          allowed.push(n);
        }
        return originalElementPrepend!.apply(this, allowed as [Node | string]);
      };
    }

    observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of Array.from(m.addedNodes)) {
          maybeBlockInTree(node);
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  function syncConsent(state: ConsentCategoryState): void {
    install();

    analyticsAllowed = !!state.analytics;
    advertisementAllowed = !!state.advertisement;
    functionalityAllowed = !!state.functionality;

    cleanupDeniedCookies();
    scheduleCleanupSweeps();
    flushAllowed();
  }

  // Install immediately with all gates denied (strict opt-in from first tick).
  install();
  cleanupDeniedCookies();
  scheduleCleanupSweeps();

  return {
    syncConsent,
    setScriptPatterns(patterns: ScriptSourcePattern[]) {
      setExtraScriptPatterns(patterns);
      // Catch anything already in the DOM that the new patterns now cover.
      // Scripts that already executed cannot be undone, but stashing them
      // stops a re-insertion and keeps replay ordering correct.
      if (typeof document !== 'undefined') {
        for (const script of Array.from(document.querySelectorAll('script'))) {
          maybeBlockScript(script);
        }
      }
    },
    flush() {
      flushAllowed();
    },
    isBlocked(cookieName: string) {
      return isBlockedName(cookieName);
    },
  };
}

/** Preferred alias. */
export const createTrackingBlocker = createSbjsBlocker;
